<template>
	<CTree :tree="tree" :layerPadding="24">
		<template v-slot="{ item }">
			<JFlex class="s-flex-col">
				<JFlex
					class="s-items-center s-py-2 s-bg-white s-rounded-lg s-shadow "
					:class="[item.layer === 1 ? 's-mt-4' : '', backgroundColorByLayer(item.layer)]"
				>
					<JFlex
						class="s-items-center s-relative s-w-full "
						:class="[item.isLast ? 's-cursor-pointer' : '']"
						@click="handleClickNode(item)"
					>
						<!-- Chivron icon -->
						<JBox
							class="s-w-10 s-ml-2 s-text-gray-500 s-cursor-pointer"
							v-if="!item.isLast"
							@click="item.isExpanded = !item.isExpanded"
						>
							<svg fill="currentColor" viewBox="0 3 15 15" class="s-rotate-90" v-if="!item.isExpanded">
								<path
									fill-rule="evenodd"
									d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
									clip-rule="evenodd"
								></path>
							</svg>
							<svg fill="currentColor" viewBox="0 2 15 15" class="s-rotate-90" v-if="item.isExpanded">
								<path
									fill-rule="evenodd"
									d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
									clip-rule="evenodd"
								></path>
							</svg>
						</JBox>

						<!-- Category -->
						<JText
							class="s-w-40 s-px-4 s-font-bold s-text-indigo-600 s-text-center s-border-r-2 s-border-gray-400 s-cursor-pointer s-select-none"
							@click="moveVariant()"
							@click.right.prevent="moveVariant()"
						>
							{{ categoryByLayer(item.layer) }}
						</JText>

						<!-- Keys' Names -->
						<JBox class="s-w-full s-text-center" v-if="item.layer === 2">
							<JInputText
								:ref="item.id"
								class="s-mx-4"
								v-model="item.key"
								v-if="onFocus === item.id"
								@blur="onFocus = ''"
								@keydown.enter="onFocus = ''"
							/>
							<JText class="s-w-full s-text-center s-cursor-pointer" v-else @click="focusInput(item.id)"> {{ item.key }}</JText>
						</JBox>
						<JText class="s-w-full s-text-center" :class="[item.layer === 1 ? 's-font-bold' : '']" v-else> {{ item.key }}</JText>

						<JBox
							class="s-w-10 s-mr-2 s-cursor-pointer"
							v-if="item.layer === 1 || item.layer === 2"
							@click="handleClickIcon(item)"
						>
							<!-- Plus Icons-->
							<JBox class="s-text-green-500" v-if="item.layer === 1">
								<svg fill="currentColor" viewBox="0 0 20 20">
									<path
										fill-rule="evenodd"
										d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
										clip-rule="evenodd"
									></path>
								</svg>
							</JBox>
							<!-- Trash Icons-->
							<JBox class="s-text-red-500 s-font-thin" v-if="item.layer === 2">
								<svg fill="currentColor" viewBox="0 0 20 20">
									<path
										fill-rule="evenodd"
										d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
										clip-rule="evenodd"
									></path>
								</svg>
							</JBox>
						</JBox>
					</JFlex>
				</JFlex>
			</JFlex>
		</template>
	</CTree>
</template>
<script>
import CTree from "@/modules/core/components/CTree"
// import { getTreeNode, removeTreeNode, reorderTreeNode } from "@/utils/treeUtils.js"
import { getTreeNode, removeTreeNode } from "@/utils/treeUtils.js"

export default {
	components: { CTree },

	props: {
		sourceTree: { type: Array },
		getTreeSkeleton: { type: Function },
		nodeValue: { type: String, default: "" }
	},

	data() {
		return {
			tree: [],
			selectedEndNode: null,
			onFocus: ""
		}
	},

	watch: {
		// Emit value to parent
		sourceTree: {
			handler(val) {
				this.tree = val
				this.selectedEndNode = null
			},
			immediate: true
		},

		selectedEndNode: {
			handler(val) {
				this.$emit("selectNode", val?.value ?? null)
			}
		},

		// Update value changes in UiEditorInspectorCss
		nodeValue: {
			handler(val) {
				if (!this.selectedEndNode) return
				this.findNode(this.selectedEndNode.id).value = val
			}
		}
	},

	methods: {
		/**
		 * <-----  Styling by Layer ----->
		 */
		backgroundColorByLayer(value) {
			return `s-bg-blue-${value - 1}00`
		},
		categoryByLayer(layer) {
			// TODO: Change to locale dependent && Rename layers
			switch (layer) {
				case 1:
					return this.$t("uiEditor.layer1")
				case 2:
					return this.$t("uiEditor.layer2")
				case 3:
					return this.$t("uiEditor.layer3")
				case 4:
					return this.$t("uiEditor.layer4")
				case 5:
					return this.$t("uiEditor.layer5")
			}
		},

		/**
		 * <----- Handling methods------>
		 */
		handleClickNode(event) {
			if (event.isLast) this.selectedEndNode = event
		},

		handleClickIcon(event) {
			if (event.layer === 1) this.addVariant(event)
			if (event.layer === 2) this.removeVariant(event.id)
		},

		/**
		 * <------ Alter variants  ------>
		 */
		addVariant(node) {
			// Generate new skeleton everytime is called for new ID
			const skeleton = this.getTreeSkeleton(node.key)
			this.tree.forEach(component => {
				if (component.key === node.key) component.children.unshift(skeleton)
			})
		},

		removeVariant(nodeId) {
			this.tree = removeTreeNode({
				nodeId,
				tree: this.tree
			})
		},

		moveVariant() {
			// arg: (id, step)
			// Method reorderTreeNode from treeUtils.js should be changed
		},

		// Focus on input field once JText is clicked
		focusInput(refName) {
			this.onFocus = refName
			this.$nextTick(() => {
				this.$refs[`${refName}`].$el.focus()
			})
		},

		/**
		 * <----- Helper Functions ----->
		 */
		// Helper : Find node by index route (e.g. route: [0,1,0,0])
		findNode(nodeId) {
			return getTreeNode({ nodeId: nodeId, tree: this.tree }) ?? {}
		}
	}
}
</script>
